import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";

import { postRow, toApiError, useSchema } from "../lib/api";
import type { ApiError, RowValue, SchemaColumn, SchemaTab } from "../lib/api";
import { formatCell } from "../lib/format";
import { Card } from "../components/Card";
import { ClayButton } from "../components/ClayButton";
import { ClayInput } from "../components/ClayInput";
import { ClaySelect } from "../components/ClaySelect";
import { EmptyState } from "../components/EmptyState";
import { ErrorState } from "../components/ErrorState";
import { LoadingBlock } from "../components/Spinner";

function editableFields(tab: SchemaTab): SchemaColumn[] {
  return tab.columns.filter((column) => !column.readOnly && column.type !== "formula");
}

function validateFields(
  fields: SchemaColumn[],
  values: Record<string, string>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of fields) {
    const raw = (values[field.key] ?? "").trim();
    if (!raw) {
      if (field.required) {
        errors[field.key] = `${field.label} wajib diisi.`;
      }
      continue;
    }
    if (field.type === "number" && !Number.isFinite(Number(raw.replace(",", ".")))) {
      errors[field.key] = `${field.label} harus berupa angka yang valid.`;
    }
  }
  return errors;
}

function labelForColumn(tab: SchemaTab, key: string): string {
  return tab.columns.find((column) => column.key === key)?.label ?? key;
}

export function InputPage() {
  const schema = useSchema();
  const inputTabs = useMemo(
    () => schema.data?.tabs.filter((tab) => tab.role === "input") ?? [],
    [schema.data],
  );

  const [tabName, setTabName] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<ApiError | null>(null);
  const [savedRow, setSavedRow] = useState<Record<string, RowValue> | null>(null);

  useEffect(() => {
    if (inputTabs.length === 0) {
      setTabName("");
      return;
    }
    setTabName((current) =>
      inputTabs.some((tab) => tab.name === current) ? current : inputTabs[0].name,
    );
  }, [inputTabs]);

  const activeTab = inputTabs.find((tab) => tab.name === tabName) ?? null;
  const fields = useMemo(() => (activeTab ? editableFields(activeTab) : []), [activeTab]);

  useEffect(() => {
    setValues({});
    setErrors({});
    setSubmitError(null);
    setSavedRow(null);
  }, [tabName]);

  const setFieldValue = (key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!current[key]) {
        return current;
      }
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!activeTab) {
      return;
    }

    const nextErrors = validateFields(fields, values);
    setErrors(nextErrors);
    setSubmitError(null);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const payload: Record<string, string | number> = {};
    for (const field of fields) {
      const raw = (values[field.key] ?? "").trim();
      if (!raw) {
        continue;
      }
      payload[field.key] = field.type === "number" ? Number(raw.replace(",", ".")) : raw;
    }

    setSubmitting(true);
    try {
      const result = await postRow(activeTab.name, payload);
      setSavedRow(result.row);
      setValues({});
      setErrors({});
    } catch (error) {
      setSubmitError(toApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (schema.loading) {
    return (
      <Card>
        <LoadingBlock message="Memuat formulir dari Google Sheet…" />
      </Card>
    );
  }

  if (schema.error) {
    return (
      <Card>
        <ErrorState error={schema.error} onRetry={schema.reload} />
      </Card>
    );
  }

  if (!activeTab) {
    return (
      <Card>
        <EmptyState
          title="Formulir input belum tersedia"
          description="Belum ada tab dengan peran input pada sumber data. Hubungi admin untuk mengatur sheet."
        />
      </Card>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
      {inputTabs.length > 1 ? (
        <Card>
          <ClaySelect
            label="Jenis laporan"
            value={tabName}
            onValueChange={setTabName}
            options={inputTabs.map((tab) => ({ value: tab.name, label: tab.label }))}
          />
        </Card>
      ) : null}

      {savedRow ? (
        <Card className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-control accent-gradient text-white shadow-clay">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12.5 10 17l9-9" />
              </svg>
            </span>
            <div>
              <h2 className="text-base font-bold text-text">Laporan berhasil dikirim</h2>
              <p className="text-sm text-muted">
                Baris berikut sudah tersimpan di sheet. Silakan lanjut mengisi laporan lain.
              </p>
            </div>
          </div>

          <dl className="grid gap-3 sm:grid-cols-2">
            {Object.entries(savedRow).map(([key, value]) => (
              <div key={key} className="rounded-control bg-bg px-4 py-3 shadow-clay-pressed">
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
                  {labelForColumn(activeTab, key)}
                </dt>
                <dd className="mt-1 text-sm font-medium text-text">
                  {formatCell(value, activeTab.columns.find((column) => column.key === key))}
                </dd>
              </div>
            ))}
          </dl>

          <div className="flex flex-wrap gap-3">
            <ClayButton variant="secondary" onClick={() => setSavedRow(null)}>
              Isi laporan lagi
            </ClayButton>
          </div>
        </Card>
      ) : null}

      <Card className="flex flex-col gap-6">
        <div>
          <h2 className="text-lg font-bold text-text">{activeTab.label}</h2>
          <p className="mt-1 text-sm text-muted">
            Kolom bertanda <span className="text-accent">*</span> wajib diisi. Kolom lain boleh
            dikosongkan.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
          <div className="grid gap-5 sm:grid-cols-2">
            {fields.map((field) => {
              const value = values[field.key] ?? "";
              const error = errors[field.key] ?? null;

              if (field.type === "select") {
                return (
                  <ClaySelect
                    key={field.key}
                    label={field.label}
                    required={field.required}
                    value={value}
                    error={error}
                    onValueChange={(next) => setFieldValue(field.key, next)}
                    options={field.options ?? []}
                    placeholder={field.required ? `Pilih ${field.label.toLowerCase()}` : "Opsional"}
                  />
                );
              }

              return (
                <ClayInput
                  key={field.key}
                  label={field.label}
                  required={field.required}
                  error={error}
                  value={value}
                  type={
                    field.type === "number"
                      ? "number"
                      : field.type === "date"
                        ? "date"
                        : field.type === "time"
                          ? "time"
                          : "text"
                  }
                  inputMode={field.type === "number" ? "decimal" : undefined}
                  step={field.type === "number" ? "any" : undefined}
                  onValueChange={(next) => setFieldValue(field.key, next)}
                />
              );
            })}
          </div>

          {submitError ? (
            <div
              role="alert"
              className="rounded-control bg-rose-500/10 px-4 py-3 text-sm text-rose-500"
            >
              <p className="font-semibold">Gagal mengirim laporan</p>
              <p className="mt-1">{submitError.message}</p>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <ClayButton type="submit" loading={submitting}>
              {submitting ? "Mengirim…" : "Kirim laporan"}
            </ClayButton>
            <ClayButton
              variant="ghost"
              disabled={submitting}
              onClick={() => {
                setValues({});
                setErrors({});
                setSubmitError(null);
              }}
            >
              Bersihkan
            </ClayButton>
          </div>
        </form>
      </Card>
    </div>
  );
}
